import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Configuration } from '../../rest/types/Configuration'
import useFooter from '../Footer/useFooter'
import useHeaderBis from '../header/useHeaderBis'
import { UserProfile } from '../../rest/types/UserProfile'
import { api } from '../../configuration'
import { LoginRegisterTemplateProps } from '../../templates/LoginRegisterTemplate'
import { UserSpeciality } from '../../rest/types/User'

import useLoginFormProps from './useLoginFormProps'
import useRegisterFormProps from './useRegisterFormProps'
import useLoginMethodFormProps from './useLoginMethodFormProps'

const useLoginOrRegisterProps = ({
  configuration,
  profiles,
  specialities,
  displayLoginFormParam,
}: {
  configuration: Configuration
  profiles: Array<UserProfile>
  specialities: Array<UserSpeciality>
  displayLoginFormParam: string | string[]
}) => {
  const headerProps = useHeaderBis(configuration, false)
  const footerProps = useFooter(configuration)
  const { loginMethodFormBlockProps, showSubsequent, goBack } =
    useLoginMethodFormProps()
  const loginFormProps = useLoginFormProps()
  const registerFormProps = useRegisterFormProps({ profiles, specialities })
  const [displayRegisterForm, setDisplayRegisterForm] = useState<boolean>(false)
  const [displayLoginForm, setDisplayLoginForm] = useState<boolean>(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (displayLoginFormParam) {
      setDisplayLoginForm(true)
    }
  }, [displayLoginFormParam])

  const props: LoginRegisterTemplateProps = useMemo(
    () => ({
      headerProps,
      footerProps,
      title: t('login_or_register_title'),
      ...(!displayLoginForm &&
        !displayRegisterForm &&
        showSubsequent && {
          bannerTextActions: {
            title: t('login_or_register_block_title'),
            blockLeft: {
              title: t('register_block_left_title'),
              subtitle: t('register_block_left_subtitle'),
              text: t('register_block_left_text'),
              ecpsConnectionButton: {
                href: `${api.API_URL}/api/connect/${api.AUTH_PROVIDER}`,
                text: t('register_ecps_block_left_button'),
              },
              mailConnectionButton: {
                text: t('register_mail_button_text'),
                onClick: () => {
                  setDisplayLoginForm(true)
                },
              },
              ecpsConnectionIssueLink: {
                label: t(
                  'register_ecps_connection_issue_block_left_link_label'
                ),
                href: t('register_ecps_connection_issue_block_left_link_href'),
              },
            },
            blockRight: {
              title: t('register_block_right_title'),
              subtitle: t('register_block_right_subtitle'),
              button: {
                text: t('register_mail_button_text'),
                onClick: () => {
                  setDisplayLoginForm(true)
                },
              },
            },
            backButton: {
              text: t('backLink_label'),
              onClick: goBack,
            },
          },
        }),
      ...(!showSubsequent && {
        loginMethodForm: loginMethodFormBlockProps,
      }),
      ...(displayRegisterForm && {
        registerForm: registerFormProps,
      }),
      ...(displayLoginForm &&
        !displayRegisterForm && {
          loginFormBlock: {
            blockTitle: t('login_register_title'),
            leftTitle: t('login_register_leftTitle'),
            rightTitle: t('login_register_button_text'),
            rightDescription: t('login_register_rightDescription'),
            rightButton: {
              text: t('login_register_button_text'),
              onClick: () => setDisplayRegisterForm(true),
            },
            form: loginFormProps,
          },
        }),
    }),
    [
      headerProps,
      footerProps,
      t,
      loginFormProps,
      registerFormProps,
      displayLoginForm,
      displayRegisterForm,
      loginMethodFormBlockProps,
      showSubsequent,
      goBack,
    ]
  )

  return props
}

export default useLoginOrRegisterProps
